import { useState, useEffect } from 'react';
import { projectService, Project, CreateProjectData } from '../../services/project.service';
import { clientService, Client } from '../../services/client.service';

const PROJECT_STATUSES = [
  { value: 'planning', label: '📋 Planificación', badge: 'badge-neutral' },
  { value: 'in_progress', label: '🚧 En Progreso', badge: 'badge-primary' },
  { value: 'on_hold', label: '⏸️ En Espera', badge: 'badge-warning' },
  { value: 'completed', label: '✅ Completado', badge: 'badge-success' },
  { value: 'cancelled', label: '❌ Cancelado', badge: 'badge-error' },
];

const INITIAL_FORM: CreateProjectData = {
  name: '',
  description: '',
  client_id: '',
  project_lead_id: '',
  address: '',
  budget: 0,
  start_date: Date.now(),
  end_date: Date.now() + 90 * 24 * 60 * 60 * 1000,
  status: 'planning',
};

export function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState<CreateProjectData>(INITIAL_FORM);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      // Cargar proyectos primero (crítico)
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);

      // Cargar clientes de forma tolerante a fallos
      // Si el rol no tiene permisos, no rompemos la página
      try {
        const clientsData = await clientService.getClients();
        setClients(clientsData);
      } catch (clientErr: any) {
        console.warn(
          'No se pudo cargar la lista completa de clientes (posible restricción de rol).',
          clientErr?.message
        );
        // Silencioso: la página sigue funcionando sin la lista completa
        setClients([]);
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    try {
      if (editingProject) {
        await projectService.updateProject(editingProject.id, formData);
      } else {
        await projectService.createProject(formData);
      }
      await loadData();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Error al guardar proyecto');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Seguro que quieres eliminar este proyecto?')) return;
    try {
      await projectService.deleteProject(id);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar proyecto');
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      client_id: project.client_id,
      project_lead_id: project.project_lead_id || '',
      address: project.address || '',
      budget: project.budget,
      start_date: project.start_date || Date.now(),
      end_date: project.end_date || Date.now() + 90 * 24 * 60 * 60 * 1000,
      status: project.status,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData(INITIAL_FORM);
    setError('');
  };

  const getStatusInfo = (status: string) => {
    return PROJECT_STATUSES.find(s => s.value === status) || PROJECT_STATUSES[0];
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Resolución de nombre del cliente:
  // 1. Preferir client_name que viene del backend (join en /api/projects)
  // 2. Fallback a la lista de clientes si estuviera disponible
  // 3. Último recurso: ID truncado
  const getClientName = (project: Project) => {
    if (project.client_name && project.client_name.trim().length > 0) {
      return project.client_name;
    }
    const client = clients.find(c => c.id === project.client_id);
    if (client) return client.name;
    return project.client_id?.slice(0, 8) || '—';
  };

  const filteredProjects = projects.filter(project => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      project.name.toLowerCase().includes(term) ||
      (project.description && project.description.toLowerCase().includes(term));
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const activeFiltersCount = filterStatus !== 'all' ? 1 : 0;

  return (
    <div>
      {/* Encabezado */}
      <div className="page-header-bar">
        <div>
          <h1>Proyectos</h1>
          <p className="subtitle">Gestión de proyectos de construcción</p>
        </div>
        <div className="actions">
          <button onClick={loadData} className="btn btn-ghost">
            🔄 Actualizar
          </button>
          <button
            onClick={() => {
              setEditingProject(null);
              setFormData(INITIAL_FORM);
              setShowModal(true);
            }}
            className="btn btn-primary"
          >
            + Nuevo Proyecto
          </button>
        </div>
      </div>

      {error && !showModal && (
        <div className="info-panel error" style={{ marginBottom: '20px' }}>
          <span className="info-panel-icon">❌</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{error}</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-search">
          <span className="toolbar-search-icon">🔍</span>
          <input
            type="text"
            className="form-input"
            placeholder="Buscar por nombre o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="toolbar-filters">
          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Todos los estados</option>
            {PROJECT_STATUSES.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>

          {activeFiltersCount > 0 && (
            <span className="filter-badge">{activeFiltersCount} filtro activo</span>
          )}
        </div>
      </div>

      {/* Contenido */}
      {isLoading && projects.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">Cargando proyectos...</p>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            {projects.length === 0 ? '📋' : '🔍'}
          </div>
          <h3 className="empty-state-title">
            {projects.length === 0 ? 'No hay proyectos registrados' : 'Sin resultados'}
          </h3>
          <p className="empty-state-text">
            {projects.length === 0
              ? 'Comienza creando el primer proyecto.'
              : 'No se encontraron proyectos con los filtros aplicados.'}
          </p>
          {projects.length === 0 && (
            <button onClick={() => setShowModal(true)} className="btn btn-primary">
              + Crear primer proyecto
            </button>
          )}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px',
          }}
        >
          {filteredProjects.map((project) => {
            const statusInfo = getStatusInfo(project.status);
            return (
              <div key={project.id} className="card">
                {/* Header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '8px',
                    marginBottom: '12px',
                  }}
                >
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                    {project.name}
                  </h3>
                  <span className={`badge ${statusInfo.badge}`}>{statusInfo.label}</span>
                </div>

                {project.description && (
                  <p
                    style={{
                      fontSize: '13px',
                      color: 'var(--gray)',
                      margin: '0 0 14px 0',
                      lineHeight: 1.5,
                    }}
                  >
                    {project.description}
                  </p>
                )}

                {/* Meta rows */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px dashed var(--light-gray)',
                    fontSize: '13px',
                  }}
                >
                  <span style={{ color: 'var(--gray)', fontWeight: 500 }}>Cliente</span>
                  <span style={{ color: '#0f172a', fontWeight: 600 }}>
                    {getClientName(project)}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    fontSize: '13px',
                  }}
                >
                  <span style={{ color: 'var(--gray)', fontWeight: 500 }}>Presupuesto</span>
                  <span style={{ color: 'var(--primary)', fontWeight: 700 }}>
                    {formatCurrency(project.budget)}
                  </span>
                </div>

                {/* Progress */}
                {project.progress !== undefined && (
                  <div style={{ marginTop: '12px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '12px',
                        color: 'var(--gray)',
                        marginBottom: '4px',
                      }}
                    >
                      <span>Avance</span>
                      <strong style={{ color: '#0f172a' }}>{project.progress}%</strong>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${Math.min(project.progress || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Fechas */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '11px',
                    color: '#94a3b8',
                    marginTop: '12px',
                  }}
                >
                  <span>Inicio: {project.start_date ? formatDate(project.start_date) : '—'}</span>
                  <span>Fin: {project.end_date ? formatDate(project.end_date) : '—'}</span>
                </div>

                {/* Actions */}
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginTop: '14px',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--light-gray)',
                  }}
                >
                  <button
                    onClick={() => handleEdit(project)}
                    className="btn-action-edit"
                    style={{ flex: 1, justifyContent: 'center' }}
                  >
                    ✏️ Editar
                  </button>
                  <button
                    onClick={() => handleDelete(project.id)}
                    className="btn-action-delete"
                    style={{ flex: 1, justifyContent: 'center' }}
                  >
                    🗑️ Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProject ? '✏️ Editar Proyecto' : '➕ Nuevo Proyecto'}</h3>
              <button onClick={handleCloseModal} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label required">Nombre del proyecto</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ej: Casa Lumina"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label">Descripción</label>
                    <textarea
                      className="form-textarea"
                      rows={2}
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descripción del proyecto..."
                    />
                  </div>
                </div>

                <div className="form-row cols-2">
                  <div className="form-col">
                    <label className="form-label required">Cliente</label>
                    <select
                      className="form-select"
                      required
                      value={formData.client_id}
                      onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    >
                      <option value="">Seleccionar cliente...</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.name} — {client.email}
                        </option>
                      ))}
                    </select>
                    {clients.length === 0 && (
                      <span className="form-help">
                        No se pudo cargar la lista de clientes. Contacta al administrador.
                      </span>
                    )}
                  </div>

                  <div className="form-col">
                    <label className="form-label">Líder de Proyecto</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.project_lead_id || ''}
                      onChange={(e) => setFormData({ ...formData, project_lead_id: e.target.value })}
                      placeholder="ID del líder (opcional)"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label">Dirección</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Calle, número, colonia, ciudad"
                    />
                  </div>
                </div>

                <div className="form-row cols-2">
                  <div className="form-col">
                    <label className="form-label required">Presupuesto (MXN)</label>
                    <input
                      type="number"
                      className="form-input"
                      required
                      min="0"
                      step="0.01"
                      value={formData.budget || ''}
                      onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-col">
                    <label className="form-label">Estado</label>
                    <select
                      className="form-select"
                      value={formData.status || 'planning'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as Project['status'] })}
                    >
                      {PROJECT_STATUSES.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row cols-2">
                  <div className="form-col">
                    <label className="form-label">Fecha de inicio</label>
                    <input
                      type="date"
                      className="form-input"
                      value={formData.start_date ? new Date(formData.start_date).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        start_date: e.target.value ? new Date(e.target.value).getTime() : undefined,
                      })}
                    />
                  </div>

                  <div className="form-col">
                    <label className="form-label">Fecha de fin</label>
                    <input
                      type="date"
                      className="form-input"
                      value={formData.end_date ? new Date(formData.end_date).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        end_date: e.target.value ? new Date(e.target.value).getTime() : undefined,
                      })}
                    />
                  </div>
                </div>

                {error && (
                  <div className="info-panel error" style={{ marginTop: '16px' }}>
                    <span className="info-panel-icon">❌</span>
                    <div className="info-panel-body">
                      <p className="info-panel-text">{error}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-ghost">
                  Cancelar
                </button>
                <button type="submit" disabled={isSaving} className="btn btn-primary">
                  {isSaving ? 'Guardando...' : editingProject ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}